import { Injectable, inject } from '@angular/core';
import * as fromSalesPlan from '@root-store/sales-plan-store/index';
import { select, Store } from '@ngrx/store';
import {
  Subject,
  takeUntil,
  debounce,
  timer,
  fromEvent,
  take,
  switchMap,
  startWith,
  filter,
  distinctUntilChanged,
  catchError,
  of,
  tap,
} from 'rxjs';
import { EventSeatSelection, EventSeatPriceApplication } from '../models/sales-plan.model';
import Konva from 'konva';
import {
  SalesPlanSeatResponseDto,
  SalesSeatStatusEnumResponseDto,
  SeatsBlockGroupResponseDto,
} from '@core/services/apiclient';
import { SynchronizeSelectionUpdateTimeout } from '../models/sales-plan-constants';
import { SalesPlanBlockViewNotificationService } from './sales-plan-blockview-notification.service';
import { SeatStatusPayloadNotification } from '../models/notification.model';
import { SalesPlanEventBlockViewUtilsService } from './sales-plan-event-blockview-utils.service';
import { LoggerService } from '@core/services/logger.service';
import { SalesEventTypeEnum } from '@sales/models/sales.model';

@Injectable({
  providedIn: 'root',
})
export class SalesPlanEventBlockViewCommunicationService {
  private readonly store = inject<Store<fromSalesPlan.State>>(Store);
  private readonly utils = inject(SalesPlanEventBlockViewUtilsService);
  private readonly notification = inject(SalesPlanBlockViewNotificationService);
  private readonly loggerService = inject(LoggerService);

  private destroy$ = new Subject<void>();
  private requestCacheRefresh$ = new Subject<string[]>();
  private selection: EventSeatSelection[] = [];
  seatLayer: Konva.Layer;
  contingentLayer: Konva.Layer;
  currentSessionId: number;

  public init(contingentLayer: Konva.Layer, seatLayer: Konva.Layer) {
    this.seatLayer = seatLayer;
    this.contingentLayer = contingentLayer;
    this.initPlanOfEventSessionNotification();
    this.getSeatSearchResult(contingentLayer);
    setTimeout(() => {
      this.getCurrentUserRemoteSeatSelection();
    }, 1000);
  }

  public clearSelection() {
    this.selection = [];
  }

  /**
   * when the current user select a seat, dispatches an action to the store, calls the api to flag the seats
   * when the api responds with 200, the store is updated and this service receives the updated current selection
   */
  public getCurrentUserRemoteSeatSelection() {
    const selection$ = this.store.pipe(select(fromSalesPlan.selectors.selectEventSeatSelection), take(1));
    // check when the user stops clicking for seat selection, wait for 2 seconds and call the store to see if local and remote selections
    // are desynchronized
    const mouseClickIdle$ = fromEvent(document, 'click').pipe(
      startWith({ ignoreDebounce: true }),
      debounce((event) => timer(event?.['ignoreDebounce'] ? 0 : SynchronizeSelectionUpdateTimeout)),
    );
    mouseClickIdle$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => selection$),
      )
      .subscribe({
        next: (value: EventSeatPriceApplication[]) => {
          this.synchronizeWithRemoteSeatSelection(value);
        },
      });
  }

  /**
   * when other users THAT ARE CONNECTED TO THE SAME PLAN select a seat, signalR broadcasts a notification to
   * the current user ONLY IF he's on the same plan as the concerned notification
   */

  public initPlanOfEventSessionNotification() {
    const sessionId$ = this.store.pipe(
      select(fromSalesPlan.selectors.selectSessionId),
      filter((id) => id !== null && id !== undefined),
      distinctUntilChanged(),
      tap((sessionId) => (this.currentSessionId = sessionId)),
    );

    sessionId$
      .pipe(
        switchMap((eventSessionId) => {
          this.loggerService.info('T&L - Session ID received:', eventSessionId);
          return this.notification.initForEventSession(eventSessionId).pipe(
            catchError((err) => {
              this.loggerService.error('T&L - Join room failed for session ID:', eventSessionId, err);
              return of(void 0);
            }),
          );
        }),
        switchMap(() => this.notification.seatStatuses$()),
        filter(
          (notifications: SeatStatusPayloadNotification[]) =>
            Array.isArray(notifications) && notifications.length > 0,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (notifications) => {
          const parentGroupIds = this.processNotifications(notifications);
          this.requestCacheRefresh(parentGroupIds);
        },
        error: (err) => {
          this.loggerService.error('T&L - SignalR broadcast notification failed:', err);
        },
      });
  }

  public getSeatSearchResult(selectionLayer: Konva.Layer) {
    this.store
      .pipe(takeUntil(this.destroy$), select(fromSalesPlan.selectors.selectSeatSearchResult))
      .subscribe({
        next: (result: SeatsBlockGroupResponseDto[]) =>
          this.utils.drawSearchResultIcons(result, selectionLayer),
      });
  }

  /**
   * calls the selection sequence: store -> effect -> api to flag the seats
   * and select the same seats locally without waiting for the api response
   */
  public updateEventSeatSelection(selection: EventSeatSelection[]) {
    if (this.isSelectionBlocked(selection)) {
      return;
    }
    this.applySelectionToPlan(selection);
    this.store.dispatch(
      fromSalesPlan.actions.updateEventSeatSelection({
        selection: selection.filter((value) => !!value).map(({ data }) => data),
      }),
    );
  }

  /**
   * called when selection/unselecting outside the plan. See sales-plan-event-basket
   */
  public updateSeatSelectionOutsidePlan(seats: SalesPlanSeatResponseDto[]) {
    if (this.isUnnumberedSeatSelectioned(seats)) {
      this.store.dispatch(
        fromSalesPlan.actions.updateEventSeatSelection({
          selection: seats,
        }),
      );
    } else {
      this.updateEventSeatSelection(this.convertToSeatSelection(seats));
    }
  }

  public refreshSeatSelectionNodes(refreshedSeats: SalesPlanSeatResponseDto[]) {
    const selectedSeats = refreshedSeats.filter((seat) =>
      this.selection.some((selectedSeat) => selectedSeat.physicalSeatId == seat.physicalSeatId),
    );
    if (!selectedSeats) {
      return;
    }
    this.removeUnselectedSeats(selectedSeats);
    const newSelectedSeats = this.convertToSeatSelection(selectedSeats);
    this.addNewSelectedSeats(newSelectedSeats);
    this.utils.highlightSelectedNodes(this.selection, this.seatLayer);
    this.requestCacheRefreshForSelection(newSelectedSeats);
  }

  private isUnnumberedSeatSelectioned(seats: any) {
    return seats?.every((seat: { type: string }) => seat.type === 'unnumbered');
  }

  /**
   * if the new selection is included in the old selection array, then we must unselect the seats
   * else we add it to the old selection
   * finally, we request the cache to refresh on the affected parent groups
   */
  private applySelectionToPlan(newSelection: EventSeatSelection[]) {
    // the newSelection elements are being deflagged
    if (this.isUnselection(newSelection)) {
      this.removeUnselectedSeats(newSelection);
      this.utils.restoreSelectedNodes(newSelection, this.seatLayer, this.contingentLayer);
    } else {
      this.addNewSelectedSeats(newSelection);
      this.utils.highlightSelectedNodes(this.selection, this.seatLayer);
    }
    this.requestCacheRefreshForSelection(newSelection);
  }

  /**
   * this function is used to synchronize local & remote selections, if we have a delta between the two
   * we must synchonize the local selection to the remote one.
   * usecase: errors, if a seat is selected locally and the api responds with KO, we must reset this selection
   */
  private synchronizeWithRemoteSeatSelection(remoteSelection: EventSeatPriceApplication[]) {
    const _remoteSelection = this.convertToSeatSelection(remoteSelection);
    const seatsToRemove = this.selection.filter(
      ({ physicalSeatId }) => !_remoteSelection.some((seat) => seat?.physicalSeatId === physicalSeatId),
    );
    const seatsToAdd = _remoteSelection.filter(
      ({ physicalSeatId }) => !this.selection.some((seat) => seat?.physicalSeatId === physicalSeatId),
    );

    if (seatsToRemove?.length) {
      this.removeUnselectedSeats(seatsToRemove);
      this.utils.restoreSelectedNodes(seatsToRemove, this.seatLayer, this.contingentLayer);
    }

    if (seatsToAdd?.length) {
      this.addNewSelectedSeats(seatsToAdd);
      this.utils.highlightSelectedNodes(this.selection, this.seatLayer);
    }

    if (seatsToRemove?.length || seatsToAdd?.length) {
      this.requestCacheRefreshForSelection([...seatsToAdd, ...seatsToRemove]);
    }
  }

  /**
   * checks if selection or unselection:
   * unselection if every targeted element is included in the selection array
   * selection if atleast one element isn't in the selection array
   */
  private isUnselection(newSelection: EventSeatSelection[]) {
    return (
      !!newSelection?.length &&
      newSelection?.length <= this.selection?.length &&
      newSelection.every((selection) =>
        this.selection.some(({ physicalSeatId }) => selection?.physicalSeatId === physicalSeatId),
      )
    );
  }

  private isSelectionBlocked(selection: EventSeatSelection[]) {
    return selection?.every((seat) => seat.data?.status !== SalesSeatStatusEnumResponseDto.Free);
  }

  private processNotifications(notifications: SeatStatusPayloadNotification[]) {
    const parentGroupIds: string[] = [];
    notifications.forEach((notification) => {
      const node = this.utils.getSeatById(notification?.seatId);
      const notifiedSeat = this.utils.eventData?.seats?.find(
        (seat) => seat.physicalSeatId === notification.seatId,
      );

      if (!notifiedSeat) {
        return;
      }

      try {
        notifiedSeat.seatIsAvailable = !notification.isBooked;
      } catch {
        this.utils.eventData.seats = this.utils.eventData.seats.map((seat) =>
          seat.physicalSeatId === notification.seatId
            ? { ...seat, seatIsAvailable: !notification.isBooked }
            : seat,
        );
      }

      if (node) {
        const seatBox = node.findOne(`#sales-seat-box-${notification.seatId}`);
        const seatNode: Konva.Group = this.utils.getSeatById(notifiedSeat?.physicalSeatId);
        seatBox.setAttrs({
          ...this.utils.getSeatConfig(notifiedSeat, true),
        });
        if (seatNode && seatNode instanceof Konva.Group) {
          this.utils.appendIconToSeat(notifiedSeat, seatNode);
          this.seatLayer.batchDraw();
        }
        if (!parentGroupIds.includes(node?.name())) {
          parentGroupIds.push(node?.name());
        }
      }
    });

    return parentGroupIds;
  }

  private addNewSelectedSeats(newSelection: EventSeatSelection[]) {
    this.selection = [...this.selection, ...newSelection]
      .filter(
        ({ physicalSeatId }, index, self) =>
          self.findIndex((seat) => seat?.physicalSeatId === physicalSeatId) === index,
      )
      .map((sel) => {
        const selectedSeat = this.selection.find((elt) => elt?.physicalSeatId === sel?.physicalSeatId);

        if (selectedSeat?.node) {
          return {
            ...selectedSeat,
            seatId: sel?.physicalSeatId,
            physicalSeatId: sel?.physicalSeatId,
          };
        }
        const seatNode: Konva.Group = this.utils.getSeatById(sel?.physicalSeatId);
        const seatBox = seatNode?.findOne(`#sales-seat-box-${sel?.physicalSeatId}`);
        if (seatBox) {
          seatBox.setAttrs({
            ...this.utils.getSeatConfig(sel?.data, false),
          });
        }
        return {
          data: sel?.data,
          seatId: sel?.physicalSeatId,
          physicalSeatId: sel?.physicalSeatId,
          node: seatNode,
        };
      });
  }

  private removeUnselectedSeats(newSelection: EventSeatSelection[] | SalesPlanSeatResponseDto[]) {
    this.selection = this.selection.filter(
      ({ physicalSeatId }) => !newSelection.some((seat) => seat?.physicalSeatId === physicalSeatId),
    );
  }

  private convertToSeatSelection(seats: SalesPlanSeatResponseDto[]): EventSeatSelection[] {
    return (seats || []).map((value) => ({
      data: value,
      node: this.utils.getSeatById(value?.physicalSeatId),
      physicalSeatId: value?.physicalSeatId,
      seatId: value?.physicalSeatId,
    }));
  }

  private requestCacheRefreshForSelection(selection: EventSeatSelection[]) {
    const parentGroupIds = selection
      .map(({ node }) => node?.name())
      .filter((groupId, index, self) => self.indexOf(groupId) === index);
    this.requestCacheRefresh(parentGroupIds);
  }

  public requestCacheRefresh(parentGroupIds: string[]) {
    this.requestCacheRefresh$.next(parentGroupIds);
  }

  public getCacheRefreshSignal() {
    return this.requestCacheRefresh$.asObservable();
  }

  public destroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.notification.leaveGroup(SalesEventTypeEnum.Event, this.currentSessionId);
  }
}
